// WPS-010 (cliente) · Asset Gallery — selector inline para BusinessCardCreator.
// Cliente filtra assets por categoría y tipo, hace click y se aplica al diseño.
import React, { useEffect, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { assetsApi, customerPhotoApi } from '../services/api';
import { Search, X, Upload, AlertTriangle } from 'lucide-react';

const TYPE_LABELS = {
  icon: 'Icons',
  photo: 'Photos',
  background: 'Backgrounds',
  pattern: 'Patterns',
};

const slugToPascal = (slug) => slug
  .split('-')
  .map(s => s.charAt(0).toUpperCase() + s.slice(1))
  .join('');

const LucideRender = ({ slug, size = 28, color = '#0f172a' }) => {
  const Comp = LucideIcons[slugToPascal(slug)];
  if (!Comp) return <span style={{ fontSize: size, color }}>·</span>;
  return <Comp size={size} color={color} />;
};

const AssetGallery = ({
  categorySlug = null,
  defaultType = 'icon',
  onSelect = () => {},
  onCustomerPhotoUpload = null,
  cardId = null,
  className = '',
}) => {
  const [assets, setAssets] = useState([]);
  const [type, setType] = useState(defaultType);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadWarning, setUploadWarning] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = { type };
      if (categorySlug) params.category = categorySlug;
      const a = await assetsApi.list(params);
      setAssets(a || []);
      setLoading(false);
    })();
  }, [type, categorySlug]);

  const filtered = assets.filter(a => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return a.name?.toLowerCase().includes(q) || a.slug?.toLowerCase().includes(q);
  });

  const handlePhotoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !cardId) return;
    setUploadingPhoto(true);
    setUploadWarning(null);
    const r = await customerPhotoApi.upload(cardId, f);
    setUploadingPhoto(false);
    e.target.value = '';
    if (r?.success) {
      if (r.data?.warning) setUploadWarning(r.data.warning);
      if (onCustomerPhotoUpload) onCustomerPhotoUpload(r.data.url);
    } else {
      alert('Upload failed');
    }
  };

  return (
    <div className={`asset-gallery border border-gray-200 ${className}`} style={{ background: '#fafafa' }}>
      {/* Toolbar */}
      <div className="border-b border-gray-200 p-3 flex flex-wrap items-center gap-2 bg-white">
        <div className="flex gap-1">
          {Object.keys(TYPE_LABELS).map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              style={{
                padding: '6px 10px',
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '10px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                border: '1px solid #e2e8f0',
                background: type === t ? '#0f172a' : '#fff',
                color: type === t ? '#fff' : '#0f172a',
                cursor: 'pointer',
              }}
            >{TYPE_LABELS[t]}</button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            style={{
              border: '1px solid #e2e8f0',
              padding: '6px 6px 6px 28px',
              fontSize: '12px',
              fontFamily: 'JetBrains Mono, monospace',
              width: '180px',
            }}
          />
        </div>
        {cardId && type === 'photo' && (
          <label style={{
            border: '1px solid #0f172a',
            padding: '6px 10px',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '10px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            background: '#0f172a',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            <Upload className="h-3 w-3" />
            {uploadingPhoto ? 'Uploading…' : 'Upload my photo'}
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploadingPhoto}
              style={{ display: 'none' }}
            />
          </label>
        )}
      </div>

      {uploadWarning && (
        <div style={{
          padding: '8px 12px',
          background: '#fef3c7',
          borderBottom: '1px solid #fbbf24',
          color: '#92400e',
          fontSize: '12px',
          fontFamily: 'JetBrains Mono, monospace',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <AlertTriangle className="h-4 w-4" />
          {uploadWarning}
        </div>
      )}

      {/* Grid */}
      <div style={{
        padding: '12px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
        gap: '8px',
        maxHeight: '320px',
        overflowY: 'auto',
      }}>
        {loading && <div className="col-span-full text-center text-gray-500 py-6 font-mono text-xs">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full text-center text-gray-500 py-6 font-mono text-xs">
            No {TYPE_LABELS[type].toLowerCase()} match{search ? ' your search' : ''}.
          </div>
        )}
        {!loading && filtered.map(a => {
          const isLucide = a.rgbPath?.startsWith('lucide:');
          const lucideSlug = isLucide ? a.rgbPath.replace('lucide:', '') : null;
          const base = (process.env.REACT_APP_BACKEND_URL || 'https://print.wunli.com').replace(/\/$/, '');
          const imgUrl = !isLucide && a.rgbPath
            ? (a.rgbPath.startsWith('http') ? a.rgbPath : `${base}${a.rgbPath}`)
            : null;

          return (
            <button
              key={a.id}
              onClick={() => onSelect(a)}
              title={a.name}
              style={{
                border: '1px solid #e2e8f0',
                background: '#fff',
                aspectRatio: '1 / 1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: '6px',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#01579b'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {isLucide ? (
                <LucideRender slug={lucideSlug} size={28} />
              ) : imgUrl ? (
                <img src={imgUrl} alt={a.name} style={{ maxWidth: '100%', maxHeight: '70%', objectFit: 'contain' }} />
              ) : (
                <span style={{ fontSize: 24, color: '#cbd5e1' }}>?</span>
              )}
              <div style={{
                fontSize: '8px',
                fontFamily: 'JetBrains Mono, monospace',
                color: '#64748b',
                marginTop: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textAlign: 'center',
              }}>{a.name?.slice(0, 14)}</div>
            </button>
          );
        })}
      </div>

      <div style={{
        padding: '8px 12px',
        borderTop: '1px solid #e2e8f0',
        background: '#fff',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '10px',
        color: '#64748b',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textAlign: 'center',
      }}>
        {filtered.length} of {assets.length} {TYPE_LABELS[type]} {categorySlug ? `· ${categorySlug}` : '· universal'}
      </div>
    </div>
  );
};

export default AssetGallery;
